import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from 'lib/auth';
import { useNotificationStore } from 'stores/notifications';
import { API_SOCKET_URL } from 'config';
import storage from 'utils/storage';

const ApiSocketContext = createContext(null);
export const useApiSocket = () => useContext(ApiSocketContext);

export const ApiSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const { addNotification } = useNotificationStore();

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [userStatuses, setUserStatuses] = useState({});
  const [userLocations, setUserLocations] = useState({});
  const reconnectRef = useRef(false);

  const token = storage.getToken();

  /** 🔌 Initialize main API socket */
  const initializeSocket = useCallback(() => {
    if (!user?._id) return;

    const socketInstance = io(API_SOCKET_URL, {
      query: { userId: user._id },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });

    /** ✅ Connected */
    socketInstance.on('connect', () => {
      console.log(`✅ API socket connected: ${socketInstance.id}`);
      setConnected(true);
    });

    /** ❌ Disconnected */
    socketInstance.on('disconnect', (reason) => {
      console.warn('❌ API socket disconnected:', reason);
      setConnected(false);
      addNotification({
        type: 'warning',
        title: 'Realtime connection lost',
      });
    });

    /** ⚠️ Connection error */
    socketInstance.on('connect_error', (err) => {
      console.error('⚠️ API socket connection error:', err);
      setConnected(false);
      addNotification({
        type: 'error',
        title: 'Realtime connection failed',
        message: err.message,
      });
    });

    /** 👥 User status updates */
    socketInstance.on('user:status:update', (data) => {
      // { userId, status: "ONLINE" | "OFFLINE" }
      setUserStatuses((prev) => ({
        ...prev,
        [data.userId]: data.status,
      }));
    });

    /** 📍 User location updates */
    socketInstance.on('user:location:changed', (data) => {
      // { userId, coordinates: [lng, lat] }
      setUserLocations((prev) => ({
        ...prev,
        [data.userId]: data.coordinates,
      }));
    });

    setSocket(socketInstance);
    reconnectRef.current = true;

    return () => socketInstance.disconnect();
  }, [user, addNotification]);

  /** 🔁 Reconnect when user changes */
  useEffect(() => {
    if (user?._id) initializeSocket();
    return () => socket?.disconnect();
  }, [user?._id]);

  /** 🚀 Emit location update */
  const emitLocationUpdate = useCallback(
    (lat, lng) => {
      if (!socket || !connected) return;
      socket.emit('user:location:update', { lat, lng });
    },
    [socket, connected]
  );

  /** 📴 Go offline manually */
  const goOffline = useCallback(() => {
    if (!socket || !connected) return;
    socket.emit('user:go:offline');
  }, [socket, connected]);

  const value = {
    socket,
    connected,
    userStatuses,
    userLocations,
    emitLocationUpdate,
    goOffline,
  };

  return <ApiSocketContext.Provider value={value}>{children}</ApiSocketContext.Provider>;
};
